// Plan de routes — remplace navigation/types.ts (les ParamList de React Navigation).
//
// ## Le changement de nature
// En natif, un paramètre d'écran était un OBJET JS arbitraire :
//   navigation.navigate("ConfirmAddress", { line1, city })
// Une URL ne transporte que des CHAÎNES. Trois destinations possibles, et le
// choix n'est pas neutre :
//
//   1. Segment d'URL (/orders/:orderId)  → identifiants. Partageable,
//      bookmarkable, survit au rechargement. À privilégier.
//   2. Query string (?mode=express)      → options d'affichage. Mêmes qualités,
//      mais alourdit l'URL — réservé à ce qui mérite d'être partagé.
//   3. `location.state`                  → objets non identifiants (une adresse
//      pré-remplie). N'apparaît PAS dans l'URL, donc PERDU au rechargement et
//      absent d'un lien copié. À n'utiliser que pour ce qui est reconstructible.
//
// ## Pourquoi ce fichier plutôt que des chaînes en dur
// Les ParamList donnaient une garantie de compilation : une faute dans un nom
// d'écran ou un paramètre manquant échouait au build. Écrire `/orders/${id}` à
// la main perd tout cela. Ces constructeurs la restituent : le typage force à
// fournir les bons paramètres, et un renommage de route se fait ici seul.
//
// Règle : AUCUN chemin littéral hors de ce fichier.

export const paths = {
  // Espace partenaire : adhésion à chaque vitrine, indépendant du rôle Keycloak.
  partners: () => "/partenaire",
  partnerSelection: () => "/partenaire?choisir=1",
  partner: (id: string) => `/partenaire/${encodeURIComponent(id)}`,
  partnerCatalog: (id: string) => `/partenaire/${encodeURIComponent(id)}/catalogue`,
  partnerDelivery: (id: string) => `/partenaire/${encodeURIComponent(id)}/livraison`,
  partnerMembers: (id: string) => `/partenaire/${encodeURIComponent(id)}/membres`,
  // ── Auth (public) ─────────────────────────────────────────────────────────
  login: () => "/login",
  /** Retour de redirection Keycloak — remplace l'écran KeycloakWebView. */
  authCallback: () => "/auth/callback",

  // ── Client — onglets ──────────────────────────────────────────────────────
  home: () => "/",
  orders: () => "/orders",
  wallet: () => "/wallet",
  profile: () => "/profile",
  /** Profil du foyer — écran authentifié, sous le shell client. */
  profilMenage: () => "/auth/profil",

  // ── Client — commandes ────────────────────────────────────────────────────
  orderDetail: (orderId: string) => `/orders/${orderId}`,
  /** Deeplink historique `sosmain://orders/:orderId/tracking`. */
  tracking: (orderId: string) => `/orders/${orderId}/tracking`,
  searchingAgent: (orderId: string) => `/orders/${orderId}/searching`,

  // ── Client — courses multi-arrêts ─────────────────────────────────────────
  /**
   * `mode=express` : le MÊME écran de composition sert les deux services — en
   * express il est paramétré à UN SEUL arrêt (« Où acheter ») et branche la
   * soumission sur buildExpressTripPayload.
   */
  newTrip: (opts?: { mode?: "express" }) => {
    const q = new URLSearchParams();
    if (opts?.mode) q.set("mode", opts.mode);
    const qs = q.toString();
    return qs ? `/trips/new?${qs}` : "/trips/new";
  },
  /**
   * Service LIVRAISON — faire porter un colis, sans achat.
   *
   * Route DISTINCTE de `newTrip` : le formulaire ne partage ni ses sections
   * (aucun article), ni sa tarification (le trajet seul). Un `?mode=` de plus
   * sur la course aurait fait diverger l'écran du service principal.
   */
  newDelivery: () => "/deliveries/new",
  tripTracking: (tripId: string) => `/trips/${tripId}`,
  tripLiveMap: (tripId: string) => `/trips/${tripId}/map`,

  // ── Client — wallet ───────────────────────────────────────────────────────
  walletRecharge: () => "/wallet/recharge",
  walletWithdraw: () => "/wallet/withdraw",
  walletHistory: () => "/wallet/history",

  // ── Client — divers ───────────────────────────────────────────────────────
  suggestions: () => "/suggestions",
  notifications: () => "/notifications",
  /** L'adresse pré-remplie passe par `location.state` (non identifiante). */
  confirmAddress: () => "/addresses/confirm",
  selectPaymentMethod: (currentMethod?: string) =>
    currentMethod
      ? `/payment-method?current=${encodeURIComponent(currentMethod)}`
      : "/payment-method",
  vip: () => "/vip",
  diaspora: () => "/diaspora",
  diasporaNewOrder: (beneficiaryId: string, orderType: "courses" | "medicaments") =>
    `/diaspora/new?beneficiary=${encodeURIComponent(beneficiaryId)}&type=${orderType}`,

  // ── Client — SOS ──────────────────────────────────────────────────────────
  /** Deeplink historique `sosmain://sos`. */
  emergency: () => "/sos",
  sosBaby: () => "/sos/baby",
  sosHealth: () => "/sos/health",
  sosTracking: (orderId: string) => `/sos/${orderId}/tracking`,

  // Le sélecteur de position n'a plus de route : il s'ouvre en surcouche via
  // `useLocationPicker().pick()`. Y naviguer démontait l'écran appelant, dont
  // l'état — adresse choisie comprise — était alors perdu (cf. locationPickerStore).

  // ── Agent ─────────────────────────────────────────────────────────────────
  agentHome: () => "/agent",
  agentMission: (missionId: string) => `/agent/missions/${missionId}`,
  agentChecklist: (missionId: string) => `/agent/missions/${missionId}/checklist`,
  /**
   * Sans `totalAmount`, contrairement au natif. Le paramètre existait dans la
   * ParamList mais l'écran ne le lisait PAS : il recalcule le total depuis les
   * `final_price` des items. Le porter aurait créé une seconde valeur pouvant
   * diverger de la commande — sur l'écran qui déclenche le débit du client.
   */
  agentEncaissement: (missionId: string) => `/agent/missions/${missionId}/encaissement`,
  agentTrip: (tripId: string) => `/agent/trips/${tripId}`,
  agentVerifyStop: (tripId: string, stopIndex: number) =>
    `/agent/trips/${tripId}/stops/${stopIndex}`,
  /**
   * Preuve photo au point de retrait — LIVRAISON uniquement.
   * Sur une COURSE, c'est `agentVerifyStop` : la route serveur correspondante
   * répond 409 si les deux sont confondues.
   */
  agentPickupProof: (tripId: string, stopIndex: number) =>
    `/agent/trips/${tripId}/stops/${stopIndex}/pickup-proof`,
  /**
   * Gains de la Main : solde retirable, détail course par course, historique
   * des retraits. Écran distinct du portefeuille CLIENT (`/wallet`) — ce ne
   * sont ni les mêmes montants, ni les mêmes routes, ni le même métier.
   */
  agentGains: () => "/agent/gains",
  /** Demander un retrait de ses gains. */
  agentPayout: () => "/agent/gains/retrait",
  agentNotifications: () => "/agent/notifications",
} as const;

/** État de navigation passé hors URL (cf. destination n°3 ci-dessus). */
export type ConfirmAddressState = { line1?: string; city?: string };
